import { useState, useEffect, useRef } from 'react';
import { Download, X, RefreshCw, AlertTriangle } from 'lucide-react';
import usePWA from '../../hooks/usePWA';

const UpdateNotification = () => {
  const { updateAvailable, updateApp, forceUpdate } = usePWA();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(false);
  const lastUpdateAvailableRef = useRef(null);

  // Get persistent dismissal status for current session
  const DISMISSAL_KEY = 'pwa-update-dismissed-current-session';

  useEffect(() => {
    // Only show if update is available, not dismissed, and this is a new update notification
    if (updateAvailable && !isDismissed) {
      // Prevent multiple rapid triggers of the same update
      if (lastUpdateAvailableRef.current === updateAvailable) {
        return;
      }
      
      // Check if this update was already dismissed in this session
      let wasDismissed = false;
      try {
        wasDismissed = sessionStorage.getItem(DISMISSAL_KEY);
      } catch (e) {
        // sessionStorage might not be available in some environments
        console.warn('SessionStorage not available for PWA update dismissal');
      }
      
      if (!wasDismissed) {
        lastUpdateAvailableRef.current = updateAvailable;
        setIsVisible(true);
        setIsDismissed(false);
        setUpdateError(false);
      }
    } else if (!updateAvailable) {
      // Clear dismissal when no update is available, so future updates can be shown
      try {
        sessionStorage.removeItem(DISMISSAL_KEY);
      } catch (e) {
        // sessionStorage might not be available
      }
      setIsVisible(false);
      setIsDismissed(false);
      setUpdateError(false);
      lastUpdateAvailableRef.current = null;
    }
  }, [updateAvailable, isDismissed]);

  const handleUpdate = async () => {
    try {
      setIsUpdating(true);
      setUpdateError(false);
      await updateApp();
    } catch (error) {
      console.warn('Update failed:', error);
      setUpdateError(true);
      setIsUpdating(false);
    }
  };
  
  const handleForceUpdate = async () => {
    try {
      setIsUpdating(true);
      setUpdateError(false);
      await forceUpdate();
    } catch (error) {
      console.warn('Force update failed:', error);
      setUpdateError(true);
      setIsUpdating(false);
    }
  };

  const handleDismiss = () => {
    // Persist dismissal to sessionStorage to prevent re-showing in same session
    try {
      sessionStorage.setItem(DISMISSAL_KEY, 'true');
    } catch (e) {
      // sessionStorage might not be available - dismiss will only work for component lifetime
    }
    
    setIsDismissed(true);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm">
      <div className="bg-azure-500 text-white rounded-lg shadow-lg p-4 flex items-center space-x-3">
        <div className="flex-shrink-0">
          <Download className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">Update Available</p>
          <p className="text-xs opacity-90">
            {updateError 
              ? 'Update failed - click Force Update to try again' 
              : 'A new version of Paper Sedge is ready'
            }
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {!updateError ? (
            <button
              onClick={handleUpdate}
              disabled={isUpdating}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-3 h-3 inline mr-1 ${isUpdating ? 'animate-spin' : ''}`} />
              {isUpdating ? 'Updating...' : 'Update'}
            </button>
          ) : (
            <button
              onClick={handleForceUpdate}
              disabled={isUpdating}
              className="bg-red-500 bg-opacity-80 hover:bg-opacity-90 px-3 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <AlertTriangle className={`w-3 h-3 inline mr-1 ${isUpdating ? 'animate-spin' : ''}`} />
              {isUpdating ? 'Forcing...' : 'Force Update'}
            </button>
          )}
          <button
            onClick={handleDismiss}
            className="text-white hover:text-azure-100 p-1"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateNotification;